import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Home, ImagePlus, Images, CreditCard, LogOut, Settings, Users, Package, Palette, Layers, Camera, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";

const UserLayout = ({ children }) => {
    const { user, logout, isAdmin } = useAuth();
    const { t } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const userNavItems = [
        { path: "/dashboard", label: t('nav_dashboard'), icon: Home },
        { path: "/generate", label: t('nav_generate'), icon: ImagePlus },
        { path: "/gallery", label: t('nav_gallery'), icon: Images },
        { path: "/purchase", label: t('nav_purchase'), icon: CreditCard },
    ];

    const adminNavItems = [
        { path: "/admin", label: t('nav_dashboard'), icon: Home },
        { path: "/admin/packages", label: t('nav_packages'), icon: Package },
        { path: "/admin/users", label: t('nav_users'), icon: Users },
        { path: "/admin/categories", label: t('nav_categories'), icon: Layers },
        { path: "/admin/backgrounds", label: t('nav_backgrounds'), icon: Palette },
        { path: "/admin/models", label: t('nav_models'), icon: Camera },
        { path: "/admin/styles", label: t('nav_styles'), icon: Sparkles },
        { path: "/admin/settings", label: t('nav_settings'), icon: Settings },
    ];

    const navItems = isAdmin ? adminNavItems : userNavItems;

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 h-16 flex items-center justify-between px-4">
                <Link to={isAdmin ? "/admin" : "/dashboard"} className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-heading font-bold text-lg">mangoplus.ai</span>
                </Link>
                <div className="flex items-center gap-2">
                    <LanguageSwitcher />
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
                        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </header>

            {/* Sidebar */}
            <aside className={`fixed top-0 left-0 z-40 h-full w-64 bg-white border-r border-gray-100 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="h-16 flex items-center justify-between px-6 border-b border-gray-100">
                    <Link to={isAdmin ? "/admin" : "/dashboard"} className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-heading font-bold text-lg">mangoplus.ai</span>
                    </Link>
                    <div className="hidden lg:block">
                        <LanguageSwitcher />
                    </div>
                </div>

                <nav className="p-4 space-y-1">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setSidebarOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                                    isActive 
                                        ? 'bg-red-50 text-red-600' 
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                            >
                                <Icon className="w-5 h-5" />
                                <span className="font-medium">{item.label}</span>
                            </Link>
                        );
                    })}
                </nav>

                {/* User Section */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100">
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 mb-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center text-white font-medium">
                            {user?.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{user?.name}</p>
                            <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                        </div>
                    </div>
                    {!isAdmin && (
                        <div className="flex items-center justify-between px-4 py-2 mb-3 bg-yellow-50 rounded-xl">
                            <span className="text-sm text-yellow-700">{t('credits')}</span>
                            <span className="font-mono font-bold text-yellow-700">{user?.credits || 0}</span>
                        </div>
                    )}
                    <Button
                        variant="ghost"
                        className="w-full justify-start text-gray-600 hover:text-red-600 hover:bg-red-50"
                        onClick={handleLogout}
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-5 h-5 mr-3" />
                        {t('logout')}
                    </Button>
                </div>
            </aside>

            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-30 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main Content */}
            <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
                <div className="p-4 md:p-8">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default UserLayout;
